import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, type LiveEventDetail, type LiveUpdateItem } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { formatDateTime, formatRelativeTime, formatTime } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { POLL_INTERVAL_MS, useEventStream } from '@/lib/use-event-stream';
import { ArticleImage } from '@/components/content';
import { Announce, Badge, Button, EmptyState, ErrorState, cn } from '@/components/ui';
import { ListSkeleton, PageHeader, Section } from './page-parts';

/**
 * `/:locale/live/:slug` — one event's timeline. PRD §12, phases.md Phase 11.
 *
 * The page is server-rendered with its first page of updates (§1.10) and then listens on
 * `/stream/live/:eventId`. Three rules from the task list govern what happens when an
 * update arrives:
 *
 * - **It prepends, highlighted.** The reader sees the new entry appear where the newest
 *   entry belongs, marked for a few seconds so it is findable.
 * - **It never moves the reader.** If they have scrolled into the timeline, the update
 *   is held back behind a "3 new updates" pill instead of pushing the paragraph they are
 *   reading down the page. Yanking a reader mid-sentence is the defining sin of live
 *   blogs.
 * - **An ended event is an archive.** The stream is not opened at all, and the page
 *   caches like any other (the `Cache-Control` decision is the server's — `ssr-data.ts`).
 */
export default function LiveEventPage() {
  const { locale, t } = useLocale();
  const slug = useParams<{ slug: string }>().slug ?? '';
  const client = useQueryClient();
  const key = queryKeys.liveEvent(locale, slug, 1);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: key,
    queryFn: () => publicApi.liveEvent(locale, slug, 1),
    staleTime: STALE.realtime,
  });

  const ended = data?.status === 'ended' || data?.status === 'archived';
  const [held, setHeld] = useState<LiveUpdateItem[]>([]);
  const [fresh, setFresh] = useState<Set<number>>(() => new Set());
  /** The last update to arrive, for the live region (Phase 17). */
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const timeline = useRef<HTMLDivElement>(null);

  /** Applies an update to the cached event, newest first and without duplicates. */
  const apply = useCallback(
    (update: LiveUpdateItem) => {
      client.setQueryData<LiveEventDetail>(key, (current) => {
        if (!current) return current;
        const rest = current.updates.filter((row) => row.id !== update.id);
        return {
          ...current,
          updates: [update, ...rest].sort(
            (a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime(),
          ),
          updatesMeta: {
            ...current.updatesMeta,
            total: current.updatesMeta.total + (rest.length === current.updates.length ? 1 : 0),
          },
        };
      });
      setFresh((current) => new Set(current).add(update.id));
      /**
       * Announced politely, not assertively: a live blog can post every ninety seconds
       * during a session of the Assembly, and interrupting a reader that often is worse
       * than telling them late. The ticker is the one place that interrupts.
       */
      setAnnouncement(`${t('live.newUpdate')}: ${update.body.slice(0, 160)}`);
      setTimeout(() => {
        setFresh((current) => {
          const next = new Set(current);
          next.delete(update.id);
          return next;
        });
      }, 4_000);
    },
    // `key` is rebuilt every render; locale and slug are what identify it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, locale, slug],
  );

  const onEvent = useCallback(
    ({ data: payload }: { data: unknown }) => {
      const event = payload as {
        type?: string;
        update?: LiveUpdateItem;
        updateId?: number;
        status?: LiveEventDetail['status'];
      };

      if (event.type === 'live.update' && event.update) {
        // Out of view, the reader is at the top and nothing of theirs moves; in view,
        // the update waits behind the pill.
        if (isScrolledIntoTimeline(timeline.current)) {
          const update = event.update;
          setHeld((current) => [update, ...current.filter((row) => row.id !== update.id)]);
        } else {
          apply(event.update);
        }
        return;
      }

      // A correction replaces in place: it is already on the reader's screen, and
      // holding it back would leave them reading the version the desk just fixed.
      if (event.type === 'live.edit' && event.update) {
        apply(event.update);
        return;
      }

      if (event.type === 'live.delete' && typeof event.updateId === 'number') {
        const removed = event.updateId;
        setHeld((current) => current.filter((row) => row.id !== removed));
        client.setQueryData<LiveEventDetail>(key, (current) =>
          current
            ? { ...current, updates: current.updates.filter((row) => row.id !== removed) }
            : current,
        );
        return;
      }

      // "This event has ended" is the one thing a reader on a stale page most needs.
      if (event.type === 'live.status' && event.status) {
        const status = event.status;
        client.setQueryData<LiveEventDetail>(key, (current) =>
          current ? { ...current, status } : current,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apply, client, locale, slug],
  );

  const { status: connection } = useEventStream({
    url: `/api/v1/stream/live/${data?.id ?? 0}`,
    // Nothing more will be posted to an ended event, and an id of 0 is a 400.
    enabled: Boolean(data?.id) && !ended,
    onEvent,
  });

  // §1.8's fallback, same as the ticker's: refetch while the stream is unavailable, and
  // through a ref so a re-render cannot keep restarting the interval.
  const poll = useRef(refetch);
  poll.current = refetch;
  useEffect(() => {
    if (connection !== 'polling') return undefined;
    const timer = setInterval(() => void poll.current(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [connection]);

  const releaseHeld = () => {
    for (const update of [...held].reverse()) apply(update);
    setHeld([]);
    timeline.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isError) {
    return (
      <Section>
        <ErrorState
          title={t('error.genericTitle')}
          body={t('error.genericBody')}
          retryLabel={t('action.retry')}
          onRetry={() => void refetch()}
        />
      </Section>
    );
  }

  if (isPending || !data) {
    return (
      <>
        <PageHeader title={t('section.liveUpdates')} />
        <Section>
          <ListSkeleton rows={8} label={t('status.loading')} />
        </Section>
      </>
    );
  }

  return (
    <>
      <EventHeader event={data} />

      <Section title={t('live.timeline')}>
        {held.length > 0 && (
          // `fixed`, not `sticky`: a sticky pill is still in the flow, and the ~47px it
          // occupies pushes the timeline down — a smaller version of exactly the shove
          // the pill exists to prevent. Found by the Phase 11 spec measuring the
          // reader's scroll position while an update was held back.
          <div className="pointer-events-none fixed inset-x-0 top-4 z-30 flex justify-center">
            <Button
              variant="primary"
              size="sm"
              className="pointer-events-auto animate-slide-in shadow-float"
              data-new-updates={held.length}
              onClick={releaseHeld}
            >
              {held.length === 1
                ? t('live.newUpdate')
                : t('live.newUpdates', { count: held.length })}
            </Button>
          </div>
        )}

        <Announce message={announcement} />

        <div ref={timeline} data-live-connection={connection}>
          {data.updates.length === 0 ? (
            <EmptyState title={t('live.noUpdates')} />
          ) : (
            <Timeline updates={data.updates} fresh={fresh} />
          )}
        </div>

        {data.updates.length < data.updatesMeta.total && (
          <OlderUpdates slug={slug} loaded={data.updates.length} />
        )}
      </Section>
    </>
  );
}

function EventHeader({ event }: { event: LiveEventDetail }) {
  const { locale, t } = useLocale();
  const live = event.status === 'live';
  const ended = event.status === 'ended' || event.status === 'archived';

  const statusLabel = {
    scheduled: t('live.statusScheduled'),
    live: t('live.statusLive'),
    ended: t('live.statusEnded'),
    archived: t('live.statusArchived'),
  }[event.status];

  const when = ended
    ? t('live.endedAt', { time: formatDateTime(event.endedAt ?? event.startTime, locale) })
    : live
      ? t('live.startedAt', { time: formatDateTime(event.startTime, locale) })
      : t('live.startsAt', { time: formatDateTime(event.startTime, locale) });

  return (
    <header className="border-b border-line bg-soft px-gutter py-7">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-7">
        {event.cover && (
          <div className="w-full shrink-0 overflow-hidden rounded-lg lg:w-80">
            <ArticleImage image={event.cover} sizes="(max-width: 1024px) 100vw, 320px" priority />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge tone={live ? 'live' : 'muted'}>{statusLabel}</Badge>
            {event.category && (
              <Badge tone="category" color={event.category.color}>
                {event.category.name}
              </Badge>
            )}
            <span className="text-xs text-muted">{when}</span>
          </div>

          <h1 className="text-4xl font-extrabold text-ink">{event.title}</h1>
          {event.description && (
            <p className="mt-2 max-w-3xl text-md text-body">{event.description}</p>
          )}
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted">
            {t('live.updateCount', { count: event.updatesMeta.total })}
          </p>
        </div>
      </div>
    </header>
  );
}

function Timeline({ updates, fresh }: { updates: LiveUpdateItem[]; fresh: Set<number> }) {
  const { locale, t } = useLocale();
  // Ticks every half minute so "4 minutes ago" ages on its own; `0` until mounted, where
  // the absolute time is shown instead (see `useNow`).
  const now = useNow(30_000);

  return (
    <ol className="flex flex-col gap-4">
      {updates.map((update) => (
        <li
          key={update.id}
          data-update-id={update.id}
          className={cn(
            'relative rounded-lg border border-line bg-white p-4 pl-6 transition',
            // The spine of the timeline, drawn on the item rather than behind the list so
            // it cannot drift out of alignment when an entry is added or removed.
            'before:absolute before:bottom-4 before:left-3 before:top-4 before:w-px before:bg-line before:content-[""]',
            update.isKeyUpdate && 'border-red/40 bg-red/[0.03]',
            fresh.has(update.id) && 'animate-slide-in ring-2 ring-red/30',
          )}
        >
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <time
              dateTime={update.postedAt}
              className="text-xs font-bold tabular-nums text-ink"
              title={formatDateTime(update.postedAt, locale)}
            >
              {now === 0
                ? formatTime(update.postedAt, locale)
                : formatRelativeTime(update.postedAt, locale, now)}
            </time>
            {update.isKeyUpdate && <Badge tone="red">{t('live.keyUpdate')}</Badge>}
          </div>

          <p className="whitespace-pre-line text-base text-body">{update.body}</p>

          {update.image && (
            <div className="mt-3 overflow-hidden rounded-md">
              <ArticleImage image={update.image} sizes="(max-width: 768px) 100vw, 640px" />
            </div>
          )}

          {update.url && (
            <a
              href={update.url}
              className="mt-3 inline-block text-sm font-semibold text-red hover:underline"
            >
              {t('live.readMore')}
            </a>
          )}
        </li>
      ))}
    </ol>
  );
}

/**
 * Older entries, fetched a page at a time and merged into the same cache entry the
 * stream writes to — so a reader who has paged back still receives new updates at the
 * top rather than quietly falling off the live feed.
 */
function OlderUpdates({ slug, loaded }: { slug: string; loaded: number }) {
  const { locale, t } = useLocale();
  const client = useQueryClient();
  const [loading, setLoading] = useState(false);
  const page = useMemo(() => Math.floor(loaded / 50) + 1, [loaded]);

  return (
    <div className="mt-6 flex justify-center">
      <Button
        variant="secondary"
        loading={loading}
        onClick={async () => {
          setLoading(true);
          try {
            const older = await publicApi.liveEvent(locale, slug, page);
            client.setQueryData<LiveEventDetail>(
              queryKeys.liveEvent(locale, slug, 1),
              (current) => {
                if (!current) return current;
                const known = new Set(current.updates.map((row) => row.id));
                return {
                  ...current,
                  updates: [...current.updates, ...older.updates.filter((r) => !known.has(r.id))],
                };
              },
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        {t('live.olderUpdates')}
      </Button>
    </div>
  );
}

/**
 * Whether the reader has scrolled into the timeline, which is the question "would
 * prepending an entry move what they are reading?" — true once the list's top has passed
 * out of the top of the viewport.
 */
function isScrolledIntoTimeline(list: HTMLDivElement | null): boolean {
  if (!list) return false;
  return list.getBoundingClientRect().top < 0;
}
